import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Eye, FileText, Filter, X, Download, ChevronRight, Trash2, Edit, AlertTriangle, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function PortalApplicationsView() {
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [colleges, setColleges] = useState([]);
  const [collegeFilter, setCollegeFilter] = useState('all');
  const [sessionFilter, setSessionFilter] = useState('all');
  const [monthFilter, setMonthFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // View Details Modal
  const [selectedApp, setSelectedApp] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [appToDelete, setAppToDelete] = useState(null);
  
  // Edit Modal
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  
  // Status Change Modal
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [statusFormData, setStatusFormData] = useState({ status: '', payment_status: '', remarks: '' });
  const [statusLoading, setStatusLoading] = useState(false);

  const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    fetchApplications();
    fetchColleges();
  }, []);

  const fetchApplications = async () => {
    try {
      let url = `${API_URL}/applications`;
      if (user?.role === 'master_agency') {
        url = `${API_URL}/master-agency/applications`;
      }
      const response = await axios.get(url, { headers: getAuthHeaders() });
      setApplications(response.data);
    } catch (error) {
      console.error('Failed to fetch applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchColleges = async () => {
    try {
      const response = await axios.get(`${API_URL}/colleges`, { headers: getAuthHeaders() });
      setColleges(response.data);
    } catch (error) {
      console.error('Failed to fetch colleges');
    }
  };

  const getAvailableSessions = () => {
    const sessions = new Set();
    applications.forEach(app => {
      if (app.admission_session) sessions.add(app.admission_session);
    });
    return Array.from(sessions).sort();
  };

  const getAvailableMonths = () => {
    const months = [];
    const currentDate = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
      months.push({
        value: `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`,
        label: date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' })
      });
    }
    return months;
  };

  const filteredApplications = useMemo(() => {
    return applications.filter(app => {
      if (statusFilter !== 'all' && app.status !== statusFilter) return false;
      if (collegeFilter !== 'all' && app.college_id !== collegeFilter) return false;
      if (sessionFilter !== 'all' && app.admission_session !== sessionFilter) return false;
      if (monthFilter !== 'all' && app.created_at) {
        const appDate = new Date(app.created_at);
        const appMonth = `${appDate.getFullYear()}-${String(appDate.getMonth() + 1).padStart(2, '0')}`;
        if (appMonth !== monthFilter) return false;
      }
      return true;
    });
  }, [applications, statusFilter, collegeFilter, sessionFilter, monthFilter]);

  const clearAllFilters = () => {
    setStatusFilter('all');
    setCollegeFilter('all');
    setSessionFilter('all');
    setMonthFilter('all');
  };

  const activeFilterCount = [statusFilter, collegeFilter, sessionFilter, monthFilter].filter(f => f !== 'all').length;

  const handleViewDetails = async (appId) => {
    try {
      const response = await axios.get(`${API_URL}/applications/${appId}`, { headers: getAuthHeaders() });
      setSelectedApp(response.data);
      setIsDetailsOpen(true);
    } catch (error) {
      toast.error('Failed to fetch application details');
    }
  };

  const handleDownloadPDF = async (appId) => {
    try {
      const response = await axios.get(`${API_URL}/applications/${appId}/download-pdf`, { headers: getAuthHeaders() });
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${response.data.content}`;
      link.download = response.data.filename;
      link.click();
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleDownloadFile = async (fileId, filename) => {
    try {
      const response = await axios.get(`${API_URL}/files/${fileId}`, { headers: getAuthHeaders() });
      const link = document.createElement('a');
      link.href = `data:application/octet-stream;base64,${response.data.content}`;
      link.download = response.data.filename || filename;
      link.click();
      toast.success('File downloaded successfully');
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const handleDeleteClick = (app) => {
    setAppToDelete(app);
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!appToDelete) return;
    try {
      let url = `${API_URL}/applications/${appToDelete.id}`;
      if (user?.role === 'master_agency') {
        url = `${API_URL}/master-agency/applications/${appToDelete.id}`;
      }
      await axios.delete(url, { headers: getAuthHeaders() });
      toast.success('Application moved to trash');
      setIsDeleteConfirmOpen(false);
      setAppToDelete(null);
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete application');
    }
  };

  const canDeleteApplication = () => {
    return user?.role === 'admin' || user?.role === 'sub_admin' || user?.role === 'master_agency';
  };

  const canEditApplication = () => {
    return user?.role === 'admin' || user?.role === 'sub_admin' || user?.role === 'master_agency';
  };

  const canChangeStatus = () => {
    return user?.role === 'admin' || user?.role === 'sub_admin' || user?.role === 'master_agency';
  };

  // Edit Application
  const handleEditClick = async (app) => {
    try {
      const response = await axios.get(`${API_URL}/applications/${app.id}`, { headers: getAuthHeaders() });
      setSelectedApp(response.data);
      setEditFormData({
        student_name: response.data.student_name || '',
        father_name: response.data.father_name || '',
        mother_name: response.data.mother_name || '',
        email: response.data.email || '',
        mobile: response.data.mobile || '',
        address: response.data.address || '',
        pincode: response.data.pincode || '',
        district: response.data.district || '',
        state: response.data.state || '',
        category: response.data.category || '',
        gender: response.data.gender || '',
        dob: response.data.dob || '',
      });
      setIsEditOpen(true);
    } catch (error) {
      toast.error('Failed to fetch application details');
    }
  };

  const handleEditSubmit = async () => {
    if (!selectedApp) return;
    setEditLoading(true);
    try {
      const formData = new FormData();
      Object.entries(editFormData).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });

      let url = `${API_URL}/applications/${selectedApp.id}`;
      if (user?.role === 'master_agency') {
        url = `${API_URL}/master-agency/applications/${selectedApp.id}`;
      }

      await axios.put(url, formData, { headers: getAuthHeaders() });
      toast.success('Application updated successfully');
      setIsEditOpen(false);
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update application');
    } finally {
      setEditLoading(false);
    }
  };

  // Status Change
  const handleStatusClick = (app) => {
    setSelectedApp(app);
    setStatusFormData({
      status: app.status || 'pending',
      payment_status: app.payment_status || 'pending',
      remarks: ''
    });
    setIsStatusOpen(true);
  };

  const handleStatusSubmit = async () => {
    if (!selectedApp) return;
    setStatusLoading(true);
    try {
      let url = `${API_URL}/applications/${selectedApp.id}/status`;
      if (user?.role === 'master_agency') {
        url = `${API_URL}/master-agency/applications/${selectedApp.id}/status`;
      }

      await axios.put(url, statusFormData, { headers: getAuthHeaders() });
      toast.success('Status updated successfully');
      setIsStatusOpen(false);
      fetchApplications();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setStatusLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'successful': case 'approved': return 'bg-green-100 text-green-700';
      case 'unsuccessful': case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-yellow-100 text-yellow-700';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'completed': case 'released': return 'bg-green-100 text-green-700';
      case 'ineligible': return 'bg-gray-100 text-gray-700';
      default: return 'bg-yellow-100 text-yellow-700';
    }
  };

  const InfoRow = ({ label, value }) => (
    <div className="grid grid-cols-3 gap-4 py-2 border-b border-slate-100">
      <div className="text-sm font-medium text-slate-500">{label}</div>
      <div className="col-span-2 text-sm text-slate-900">{value || '-'}</div>
    </div>
  );

  const DocumentDownloadRow = ({ label, fileId }) => fileId ? (
    <div className="flex items-center justify-between p-2 border border-slate-200 rounded">
      <span className="text-sm text-slate-700">{label}</span>
      <Button size="sm" variant="outline" onClick={() => handleDownloadFile(fileId, label)}>
        <Download className="w-3 h-3 mr-1" /> Download
      </Button>
    </div>
  ) : null;

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm" data-testid="portal-applications-view">
      {/* Header */}
      <div className="flex justify-between items-center p-4 border-b border-slate-200">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold text-slate-900">Admission Applications</h2>
          <span className="bg-purple-100 text-purple-700 text-xs font-medium px-2 py-1 rounded-full">
            {filteredApplications.length} of {applications.length}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={showFilters ? "default" : "outline"}
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className={showFilters ? "bg-purple-600 hover:bg-purple-700 text-white" : ""}
            data-testid="toggle-filters-btn"
          >
            <Filter className="w-4 h-4 mr-1" />
            Filters
            {activeFilterCount > 0 && (
              <span className="ml-1 bg-white text-purple-700 text-xs px-1.5 rounded-full">{activeFilterCount}</span>
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate('/applications')}
            data-testid="view-all-applications-btn"
          >
            View All
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="p-4 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-slate-700">Filter Applications</span>
            {activeFilterCount > 0 && (
              <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-slate-500 hover:text-slate-700 h-7">
                <X className="w-3 h-3 mr-1" /> Clear All
              </Button>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {/* College Filter */}
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">College / University</Label>
              <Select value={collegeFilter} onValueChange={setCollegeFilter}>
                <SelectTrigger className="h-9" data-testid="portal-college-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Colleges</SelectItem>
                  {colleges.map(college => (
                    <SelectItem key={college.id} value={college.id}>{college.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Session Filter */}
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Admission Session</Label>
              <Select value={sessionFilter} onValueChange={setSessionFilter}>
                <SelectTrigger className="h-9" data-testid="portal-session-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sessions</SelectItem>
                  {getAvailableSessions().map(session => (
                    <SelectItem key={session} value={session}>{session}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Month Filter */}
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Submission Month</Label>
              <Select value={monthFilter} onValueChange={setMonthFilter}>
                <SelectTrigger className="h-9" data-testid="portal-month-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Months</SelectItem>
                  {getAvailableMonths().map(month => (
                    <SelectItem key={month.value} value={month.value}>{month.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Status Filter */}
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="h-9" data-testid="portal-status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      {/* Applications Table */}
      {loading ? (
        <div className="text-center py-8 text-slate-500">Loading applications...</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="portal-applications-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="h-10 px-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap">App No.</th>
                <th className="h-10 px-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap">Enrollment</th>
                <th className="h-10 px-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap">Student</th>
                <th className="h-10 px-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap">College</th>
                <th className="h-10 px-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap">Session</th>
                {(user?.role === 'admin' || user?.role === 'sub_admin' || user?.role === 'master_agency') && (
                  <th className="h-10 px-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap">Agency</th>
                )}
                <th className="h-10 px-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap">Status</th>
                <th className="h-10 px-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap">Payment</th>
                <th className="h-10 px-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider whitespace-nowrap">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredApplications.slice(0, 10).map((app) => (
                <tr key={app.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`portal-app-row-${app.id}`}>
                  <td className="p-3 text-sm text-slate-700 font-medium whitespace-nowrap">{app.application_number}</td>
                  <td className="p-3 text-sm whitespace-nowrap">
                    {app.enrollment_number ? (
                      <span className="inline-block px-2 py-0.5 text-xs rounded bg-purple-100 text-purple-700 font-medium">{app.enrollment_number}</span>
                    ) : (
                      <span className="text-slate-400 text-xs">-</span>
                    )}
                  </td>
                  <td className="p-3 text-sm text-slate-700 whitespace-nowrap">{app.student_name}</td>
                  <td className="p-3 text-sm text-slate-600 whitespace-nowrap max-w-[150px] truncate" title={app.college_name}>{app.college_name}</td>
                  <td className="p-3 text-sm text-slate-600 whitespace-nowrap">{app.admission_session}</td>
                  {(user?.role === 'admin' || user?.role === 'sub_admin' || user?.role === 'master_agency') && (
                    <td className="p-3 text-sm text-slate-600 whitespace-nowrap">{app.agency_name}</td>
                  )}
                  <td className="p-3 text-sm whitespace-nowrap">
                    <span className={`inline-block px-2 py-0.5 text-xs rounded ${getStatusColor(app.status)}`}>
                      {app.status.toUpperCase()}
                    </span>
                  </td>
                  <td className="p-3 text-sm whitespace-nowrap">
                    <span className={`inline-block px-2 py-0.5 text-xs rounded ${getPaymentStatusColor(app.payment_status)}`}>
                      {app.payment_status.toUpperCase()}
                    </span>
                  </td>
                  <td className="p-3 text-sm whitespace-nowrap">
                    <div className="flex gap-1">
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => handleViewDetails(app.id)} 
                        data-testid={`portal-view-app-${app.id}`}
                        className="h-7 w-7 p-0"
                        title="View Details"
                      >
                        <Eye className="w-4 h-4 text-slate-600" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        onClick={() => handleDownloadPDF(app.id)}
                        className="h-7 w-7 p-0"
                        title="Download PDF"
                      >
                        <FileText className="w-4 h-4 text-slate-600" />
                      </Button>
                      {canEditApplication() && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleEditClick(app)}
                          className="h-7 w-7 p-0 hover:bg-blue-100"
                          title="Edit Application"
                          data-testid={`portal-edit-app-${app.id}`}
                        >
                          <Edit className="w-4 h-4 text-blue-500" />
                        </Button>
                      )}
                      {canChangeStatus() && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleStatusClick(app)}
                          className="h-7 w-7 p-0 hover:bg-green-100"
                          title="Change Status"
                          data-testid={`portal-status-app-${app.id}`}
                        >
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        </Button>
                      )}
                      {canDeleteApplication() && (
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleDeleteClick(app)}
                          className="h-7 w-7 p-0 hover:bg-red-100"
                          title="Delete Application"
                          data-testid={`portal-delete-app-${app.id}`}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          
          {filteredApplications.length === 0 && (
            <div className="text-center py-8">
              <p className="text-slate-500 text-sm">No applications match your filters.</p>
              {activeFilterCount > 0 && (
                <Button variant="outline" size="sm" onClick={clearAllFilters} className="mt-2">
                  Clear Filters
                </Button>
              )}
            </div>
          )}
          
          {filteredApplications.length > 10 && (
            <div className="text-center py-3 border-t border-slate-100">
              <Button variant="link" size="sm" onClick={() => navigate('/applications')} className="text-purple-600">
                View all {filteredApplications.length} applications
              </Button>
            </div>
          )}
        </div>
      )}

      {/* View Details Modal */}
      <Dialog open={isDetailsOpen} onOpenChange={setIsDetailsOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Application Details - {selectedApp?.application_number}</DialogTitle>
          </DialogHeader>
          {selectedApp && (
            <div className="space-y-4">
              {/* Enrollment Number Display */}
              {selectedApp.enrollment_number && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-purple-600 font-medium">ENROLLMENT NUMBER</p>
                  <p className="text-xl font-bold text-purple-700">{selectedApp.enrollment_number}</p>
                </div>
              )}

              {/* Admission Details */}
              <div>
                <h3 className="text-md font-semibold text-slate-900 mb-2 pb-1 border-b">Admission Details</h3>
                <div className="grid grid-cols-2 gap-x-6">
                  <InfoRow label="College/University" value={selectedApp.college_name} />
                  <InfoRow label="Session" value={selectedApp.admission_session} />
                  <InfoRow label="Course" value={`${selectedApp.course} - ${selectedApp.specialization}`} />
                  <InfoRow label="Admission Type" value={selectedApp.admission_type} />
                </div>
              </div>

              {/* Personal Details */}
              <div>
                <h3 className="text-md font-semibold text-slate-900 mb-2 pb-1 border-b">Personal Details</h3>
                <div className="grid grid-cols-2 gap-x-6">
                  <InfoRow label="Student Name" value={selectedApp.student_name} />
                  <InfoRow label="Father's Name" value={selectedApp.father_name} />
                  <InfoRow label="Mother's Name" value={selectedApp.mother_name} />
                  <InfoRow label="Email" value={selectedApp.email} />
                  <InfoRow label="Mobile" value={selectedApp.mobile} />
                  <InfoRow label="DOB" value={selectedApp.dob} />
                  <InfoRow label="Gender" value={selectedApp.gender} />
                  <InfoRow label="Category" value={selectedApp.category} />
                </div>
              </div>

              {/* Address */}
              <div>
                <h3 className="text-md font-semibold text-slate-900 mb-2 pb-1 border-b">Address</h3>
                <InfoRow label="Address" value={selectedApp.address} />
                <div className="grid grid-cols-3 gap-x-6">
                  <InfoRow label="Pincode" value={selectedApp.pincode} />
                  <InfoRow label="District" value={selectedApp.district} />
                  <InfoRow label="State" value={selectedApp.state} />
                </div>
              </div>

              {/* Academic Records */}
              {selectedApp.academic_records && selectedApp.academic_records.length > 0 && (
                <div>
                  <h3 className="text-md font-semibold text-slate-900 mb-2 pb-1 border-b">Academic Details</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-slate-200 text-sm">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="border px-2 py-1 text-left text-xs">Exam</th>
                          <th className="border px-2 py-1 text-left text-xs">Subject</th>
                          <th className="border px-2 py-1 text-left text-xs">Year</th>
                          <th className="border px-2 py-1 text-left text-xs">Board</th>
                          <th className="border px-2 py-1 text-left text-xs">Obtained</th>
                          <th className="border px-2 py-1 text-left text-xs">Max</th>
                          <th className="border px-2 py-1 text-left text-xs">%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedApp.academic_records.map((r, i) => (
                          <tr key={i}>
                            <td className="border px-2 py-1">{r.exam_name}</td>
                            <td className="border px-2 py-1">{r.subject}</td>
                            <td className="border px-2 py-1">{r.year}</td>
                            <td className="border px-2 py-1">{r.board_university}</td>
                            <td className="border px-2 py-1">{r.obtained_marks}</td>
                            <td className="border px-2 py-1">{r.max_marks}</td>
                            <td className="border px-2 py-1 font-medium">{r.percentage_grade}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Documents */}
              <div>
                <h3 className="text-md font-semibold text-slate-900 mb-2 pb-1 border-b">Documents</h3>
                <div className="grid grid-cols-2 gap-2">
                  <DocumentDownloadRow label="Student Photo" fileId={selectedApp.student_photo_id} />
                  <DocumentDownloadRow label="Student Signature" fileId={selectedApp.student_signature_id} />
                  <DocumentDownloadRow label="Address/ID Proof" fileId={selectedApp.address_id_proof_id} />
                  <DocumentDownloadRow label="ABC/DEB Screenshot" fileId={selectedApp.abc_deb_screenshot_id} />
                  <DocumentDownloadRow label="10th Marksheet" fileId={selectedApp.tenth_marksheet_id} />
                  <DocumentDownloadRow label="12th Marksheet" fileId={selectedApp.twelfth_marksheet_id} />
                  <DocumentDownloadRow label="Graduation Marksheet" fileId={selectedApp.graduation_marksheet_id} />
                </div>
              </div>

              {/* Status */}
              <div className="flex gap-4 pt-2 border-t">
                <div>
                  <span className="text-xs text-slate-500">Status:</span>
                  <span className={`ml-2 px-2 py-0.5 text-xs rounded ${getStatusColor(selectedApp.status)}`}>
                    {selectedApp.status.toUpperCase()}
                  </span>
                </div>
                <div>
                  <span className="text-xs text-slate-500">Payment:</span>
                  <span className={`ml-2 px-2 py-0.5 text-xs rounded ${getPaymentStatusColor(selectedApp.payment_status)}`}>
                    {selectedApp.payment_status.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteConfirmOpen} onOpenChange={setIsDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Delete Application
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              Are you sure you want to delete application <strong>{appToDelete?.application_number}</strong>?
            </p>
            <p className="text-sm text-slate-500 mt-2">
              This will move the application to Trash. It can be restored by Admin.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteConfirmOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleDeleteConfirm} 
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-btn"
            >
              Delete Application
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Application Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5 text-blue-600" />
              Edit Application - {selectedApp?.application_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Student Name</Label>
                <Input 
                  value={editFormData.student_name || ''} 
                  onChange={(e) => setEditFormData({...editFormData, student_name: e.target.value})}
                  data-testid="edit-student-name"
                />
              </div>
              <div>
                <Label>Father's Name</Label>
                <Input 
                  value={editFormData.father_name || ''} 
                  onChange={(e) => setEditFormData({...editFormData, father_name: e.target.value})}
                />
              </div>
              <div>
                <Label>Mother's Name</Label>
                <Input 
                  value={editFormData.mother_name || ''} 
                  onChange={(e) => setEditFormData({...editFormData, mother_name: e.target.value})}
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input 
                  type="email"
                  value={editFormData.email || ''} 
                  onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                />
              </div>
              <div>
                <Label>Mobile</Label>
                <Input 
                  value={editFormData.mobile || ''} 
                  onChange={(e) => setEditFormData({...editFormData, mobile: e.target.value})}
                />
              </div>
              <div>
                <Label>Date of Birth</Label>
                <Input 
                  type="date"
                  value={editFormData.dob || ''} 
                  onChange={(e) => setEditFormData({...editFormData, dob: e.target.value})}
                />
              </div>
              <div>
                <Label>Gender</Label>
                <Select value={editFormData.gender || ''} onValueChange={(v) => setEditFormData({...editFormData, gender: v})}>
                  <SelectTrigger><SelectValue placeholder="Select Gender" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category</Label>
                <Select value={editFormData.category || ''} onValueChange={(v) => setEditFormData({...editFormData, category: v})}>
                  <SelectTrigger><SelectValue placeholder="Select Category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="General">General</SelectItem>
                    <SelectItem value="OBC">OBC</SelectItem>
                    <SelectItem value="SC">SC</SelectItem>
                    <SelectItem value="ST">ST</SelectItem>
                    <SelectItem value="EWS">EWS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Address</Label>
              <Textarea 
                value={editFormData.address || ''} 
                onChange={(e) => setEditFormData({...editFormData, address: e.target.value})}
                rows={2}
              />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Pincode</Label>
                <Input 
                  value={editFormData.pincode || ''} 
                  onChange={(e) => setEditFormData({...editFormData, pincode: e.target.value})}
                />
              </div>
              <div>
                <Label>District</Label>
                <Input 
                  value={editFormData.district || ''} 
                  onChange={(e) => setEditFormData({...editFormData, district: e.target.value})}
                />
              </div>
              <div>
                <Label>State</Label>
                <Input 
                  value={editFormData.state || ''} 
                  onChange={(e) => setEditFormData({...editFormData, state: e.target.value})}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleEditSubmit} 
              disabled={editLoading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="submit-edit-btn"
            >
              {editLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Status Change Dialog */}
      <Dialog open={isStatusOpen} onOpenChange={setIsStatusOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              Change Status - {selectedApp?.application_number}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Application Status</Label>
              <Select value={statusFormData.status} onValueChange={(v) => setStatusFormData({...statusFormData, status: v})}>
                <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="successful">Successful</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="unsuccessful">Unsuccessful</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Payment Status</Label>
              <Select value={statusFormData.payment_status} onValueChange={(v) => setStatusFormData({...statusFormData, payment_status: v})}>
                <SelectTrigger data-testid="payment-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="released">Released</SelectItem>
                  <SelectItem value="ineligible">Ineligible</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Remarks (Optional)</Label>
              <Textarea 
                value={statusFormData.remarks} 
                onChange={(e) => setStatusFormData({...statusFormData, remarks: e.target.value})}
                placeholder="Add any remarks..."
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsStatusOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleStatusSubmit} 
              disabled={statusLoading}
              className="bg-green-600 hover:bg-green-700 text-white"
              data-testid="submit-status-btn"
            >
              {statusLoading ? 'Updating...' : 'Update Status'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
